/*
 * Free shipping progress for the announcement bar.
 * One shared controller reads the cart subtotal (Rebuy Smart Cart when present, otherwise /cart.js)
 * and pushes it to every <shipping-announcement> instance, including ticker/slider clones.
 */
(function () {
  'use strict';

  if (customElements.get('shipping-announcement')) return;

  const instances = new Set();
  let subtotal = null;
  let itemCount = null;
  let rebuyThreshold = null;
  let fetchController = null;

  const formatMoney = (cents) => {
    let money = window.theme && window.theme.formatMoney ? window.theme.formatMoney(cents, window.theme.moneyFormat) : (cents / 100).toFixed(2);
    if (money.endsWith('.00') || money.endsWith(',00')) money = money.slice(0, -3);
    return money;
  };

  // Free shipping minimum configured on the Rebuy tiered progress bar (shop currency, whole units)
  const getRebuyThreshold = () => {
    try {
      const components = window.Rebuy.SmartCart.settings.components || [];
      const bar = components.find((component) => component.type === 'tiered_progress_bar');
      const tier = bar.settings.bars[0].tiers.find((item) => item.type === 'shipping');
      const rate = parseFloat((window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1);
      return tier ? Math.round(Number(tier.minimum) * 100 * rate) : null;
    } catch (error) {
      return null;
    }
  };

  // Same subtotal the Rebuy progress bar uses (respects its excluded products / discount settings)
  const getRebuySubtotal = () => {
    try {
      const smartCart = window.Rebuy.SmartCart;
      if (!smartCart.cart) return null;
      const value = Number(smartCart.shippingExcludedSubtotal());
      return Number.isFinite(value) ? value : null;
    } catch (error) {
      return null;
    }
  };

  const render = () => instances.forEach((instance) => instance.update());

  const setCart = (cart) => {
    const rebuySubtotal = getRebuySubtotal();
    const nextThreshold = getRebuyThreshold();
    if (nextThreshold) rebuyThreshold = nextThreshold;

    if (cart) {
      itemCount = cart.item_count;
      subtotal = rebuySubtotal !== null ? rebuySubtotal : cart.total_price;
    } else if (rebuySubtotal !== null) {
      subtotal = rebuySubtotal;
      itemCount = window.Rebuy.SmartCart.itemCount();
    }

    render();
  };

  const refreshCart = () => {
    if (fetchController) fetchController.abort();
    fetchController = new AbortController();

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(`${root}cart.js`, {signal: fetchController.signal})
      .then((response) => response.json())
      .then((cart) => setCart(cart))
      .catch(() => {});
  };

  // Rebuy passes its cart on the event; theme events need a fresh /cart.js
  const onRebuyChange = (event) => {
    const cart = event.detail && event.detail.cart && (event.detail.cart.cart || event.detail.cart);
    if (cart && typeof cart.total_price !== 'undefined') {
      setCart(cart);
    } else {
      refreshCart();
    }
  };

  ['rebuy:cart.change', 'rebuy:cart.ready', 'rebuy:smartcart.ready'].forEach((name) => document.addEventListener(name, onRebuyChange));
  ['theme:cart:added', 'theme:cart:update', 'theme:cart:refresh', 'theme:cart:change'].forEach((name) => document.addEventListener(name, refreshCart));
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refreshCart();
  });

  customElements.define(
    'shipping-announcement',
    class extends HTMLElement {
      connectedCallback() {
        this.syncRebuy = this.dataset.syncRebuy === 'true';
        this.progress = this.querySelector('.shipping-announcement__progress');

        // Only the real slide announces changes, not the ticker comparitor/marquee clones
        if (this.closest('[data-clone], .ticker__comparitor')) {
          this.removeAttribute('aria-live');
        } else {
          this.setAttribute('aria-live', 'polite');
        }

        instances.add(this);
        this.update();
      }

      disconnectedCallback() {
        instances.delete(this);
      }

      update() {
        if (subtotal === null) return;

        const threshold = (this.syncRebuy && rebuyThreshold) || Number(this.dataset.threshold);
        const remaining = Math.max(threshold - subtotal, 0);
        let state = 'progress';
        if (!itemCount) {
          state = 'empty';
        } else if (subtotal >= threshold) {
          state = 'reached';
        }

        this.querySelectorAll('[data-shipping-remaining]').forEach((el) => (el.textContent = formatMoney(remaining)));
        this.querySelectorAll('[data-shipping-threshold]').forEach((el) => (el.textContent = formatMoney(threshold)));
        this.style.setProperty('--shipping-progress', `${Math.min((subtotal / threshold) * 100, 100)}%`);

        if (this.dataset.state !== state) {
          this.dataset.state = state;
          // Let the ticker re-measure since the visible message width changed
          const ticker = this.closest('ticker-bar');
          if (ticker && typeof ticker.resizeEvent === 'function') ticker.resizeEvent();
        }
      }
    }
  );

  // Rebuy may already be loaded; otherwise its ready events will sync us
  if (getRebuySubtotal() !== null) {
    setCart(null);
  }
})();
